import {
  fireQuotesShown,
  flattenGroupedPrices,
} from 'analytics/bookingEvents'
import { Box, CircularProgress, Typography } from '@mui/material'
import Modal from '@mui/material/Modal'
import { jobsAPI } from 'api/jobsAPI'
import useQuote from 'hooks/useQuote'
import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import Header from './Header'
import Quotes from './Quotes'

type ReallocateModalProps = {
  isReallocateModalOpen: boolean
  setIsReallocateModalOpen: (e: boolean) => void
  selectedJob: any
}

const ReallocateModal = ({
  isReallocateModalOpen,
  setIsReallocateModalOpen,
  selectedJob,
}: ReallocateModalProps) => {
  const { data } = useQuery({
    queryKey: ['selectedJob', selectedJob?.id],
    queryFn: async () => jobsAPI.getJobById(selectedJob?.id),
  })
  const { prices, reference, createReallocateQuote, isLoading, error } =
    useQuote()

  useEffect(() => {
    if (!selectedJob?.id || !data?.data?.supplier?.companyName) {
      return
    }

    if (isReallocateModalOpen && !isLoading) {
      createReallocateQuote(
        selectedJob?.id,
        data?.data?.vehicleDetails?.vehicleTypeName,
        data?.data?.supplier?.companyName,
      )
    }
  }, [isReallocateModalOpen])

  const quotesShownFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (!prices || !reference) return
    if (quotesShownFiredRef.current === reference) return
    quotesShownFiredRef.current = reference
    fireQuotesShown({
      surface: 'reallocate',
      quoteGroupId: reference,
      quotes: flattenGroupedPrices(prices),
    })
  }, [prices, reference])

  return (
    <Modal
      open={isReallocateModalOpen}
      onClose={() => setIsReallocateModalOpen(false)}
      aria-labelledby="reallocate"
      aria-describedby="Reallocate booking modal"
    >
      <Box
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '40%',
          minWidth: '300px',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        <Header
          setIsReallocateModalOpen={setIsReallocateModalOpen}
          selectedJob={data?.data}
        />
        <Box
          component="div"
          sx={{
            backgroundColor: '#FFFFFF',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '20px',
            height: '65vh',
            overflowY: 'scroll',
          }}
        >
          <Box component="div" sx={{ width: '100%', paddingBottom: '20px' }}>
            {isLoading && (
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Typography component="p" sx={{ color: '#000' }}>
                  Loading quotes
                </Typography>
                <CircularProgress color="secondary" />
              </Box>
            )}
            {!isLoading && error && (
              <Typography
                component="p"
                sx={(theme) => ({
                  color: theme.palette.error.main,
                  textAlign: 'center',
                })}
              >
                Error getting quotes.
              </Typography>
            )}
            {!isLoading &&
              !error &&
              prices &&
              Object.entries(prices).map((key: any) => (
                <Quotes
                  key={key[0]}
                  type={key[0]}
                  quotes={key[1]}
                  bookingVehicleId={selectedJob?.id}
                  quoteReference={reference}
                  setIsReallocateModalOpen={setIsReallocateModalOpen}
                  previousPrice={
                    data?.data?.estimatedPrice?.total ??
                    data?.data?.estimatedPrice?.price
                  }
                />
              ))}
            {!isLoading && !error && !prices && (
              <Typography
                component="p"
                sx={{
                  textAlign: 'center',
                }}
              >
                Sorry there are no quotes available for this journey.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default ReallocateModal
